import moment from 'moment';
import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { ThemeColors } from 'styled-components';

import { AppleLogoIcon, GoogleLogoIcon, MicrosoftOutlookIcon } from '@treams/treams-icons';
import {
	AttendeeProposal,
	AttendeeStatus,
	MeetingFragment,
	MeetingSeriesRecurrenceFrequency,
	MeetingTargetInput,
	MeetingTargetType,
} from '@treams/treams-network';
import {
	Avatar,
	Button,
	Callout,
	FlexGap,
	GraphQLErrorMessage,
	IconWrapper,
	Modal,
	Paragraph,
	Popover,
	Separator,
} from '@treams/treams-ui';

import { DateTimeInput } from './DateTimeInput';
import { DurationInput } from './DurationInput';
import { MeetingProposals } from './MeetingProposals';
import MeetingSeriesConfirmationModal from './MeetingSeriesConfirmationModal';
import { SeriesInput } from './SeriesInput';
import useMeetingMutations, { type RequiredSeriesMetadata } from './hooks/useMeetingMutations';
import useMeetingTimezone from './hooks/useMeetingTimezone';

export enum ConfirmActionType {
	Update,
	Archive,
	Cancel,
}

const ATTENDEE_STATUS_BORDER_COLOR: Record<AttendeeStatus, keyof ThemeColors> = {
	[AttendeeStatus.Accepted]: 'green',
	[AttendeeStatus.Declined]: 'danger',
	[AttendeeStatus.Tentative]: 'yellow',
	[AttendeeStatus.NeedsAction]: 'whiteBlueLight',
};

export function MeetingModal<T extends MeetingTargetType>(
	props: {
		title: string;
		onClose: () => void;
		meeting?: MeetingFragment | undefined | null;
		target: MeetingTargetInput;
		attendeesLabel?: string;
		attendees: ComponentProps<typeof Avatar.Stack>['users'];
		defaultDuration?: number;
		seriesMetadata: RequiredSeriesMetadata<T>;
		canUpdate?: boolean;
	} & Pick<ComponentProps<typeof SeriesInput>, 'renderSeriesMetadataFields'>,
) {
	const { t } = useTranslation();

	const meeting = props.meeting;
	const { upsert, cancel, restore, error } = useMeetingMutations({
		target: props.target,
		hasMeeting: !!meeting,
	});

	const defaultDuration = props.defaultDuration ?? 30;
	const defaults = useFieldDefaults(meeting, defaultDuration);

	const { meetingTimezone, localTimezone, meetingCity, startsAtFormatted } =
		useMeetingTimezone(meeting);

	const [startsAt, setStartsAt] = useState<Date>(defaults.startsAt);
	const [endsAt, setEndsAt] = useState<Date>(defaults.endsAt);
	const duration = useDuration(startsAt, endsAt, defaultDuration);

	const [recurrenceFrequency, setRecurrenceFrequency] = useState<
		MeetingSeriesRecurrenceFrequency | undefined
	>(defaults.recurrenceFrequency);

	const [confirm, setConfirm] = useState<ConfirmActionType | undefined>(undefined);

	const onChangeStartsAt = useCallback(
		(value: Date) => {
			const newEndsAt = moment(value).add(duration, 'minutes').toDate();
			setStartsAt(value);
			setEndsAt(newEndsAt);
		},
		[duration],
	);

	const onChangeDuration = useCallback(
		(value: number) => {
			const newEndsAt = moment(startsAt).add(value, 'minutes').toDate();
			setEndsAt(newEndsAt);
		},
		[startsAt],
	);

	const doUpsert = useCallback(
		async (
			irregularOccurrence?: boolean,
			override?: {
				startsAt: Date;
				endsAt: Date | null;
				recurrenceFrequency: MeetingSeriesRecurrenceFrequency | undefined;
			},
		) => {
			const frequency = override?.recurrenceFrequency ?? recurrenceFrequency;

			const success = await upsert({
				startsAt: (override?.startsAt ?? startsAt).toISOString(),
				endsAt: (override?.endsAt ?? endsAt).toISOString(),
				timezone: meetingTimezone,
				series: frequency
					? {
							recurrenceFrequency: frequency,
							irregularOccurrence,
							...props.seriesMetadata,
						}
					: undefined,
			});

			if (success) {
				props.onClose();
			}
		},
		[endsAt, props, recurrenceFrequency, startsAt, meetingTimezone, upsert],
	);

	const onClickSave = useCallback(async () => {
		const hasExistingSeries = !!meeting?.series;
		const willHaveSeries = recurrenceFrequency !== undefined;
		const startsAtChanged = startsAt !== defaults.startsAt;
		const durationChanged = duration !== defaults.duration;

		if (hasExistingSeries && willHaveSeries && (startsAtChanged || durationChanged)) {
			setConfirm(ConfirmActionType.Update);
			return;
		}

		if (hasExistingSeries && !willHaveSeries) {
			setConfirm(ConfirmActionType.Archive);
			return;
		}

		await doUpsert();
	}, [doUpsert, duration, defaults, startsAt, meeting?.series, recurrenceFrequency]);

	const onAcceptProposal = useCallback(
		async ({ proposal }: { proposal: AttendeeProposal }) => {
			const startsAt = moment(proposal.startsAt).toDate();
			const endsAt = moment(proposal.endsAt).toDate();

			// Resets recurrence frequency to default if the field was previously dirty
			const recurrenceFrequency = defaults.recurrenceFrequency;

			setStartsAt(startsAt);
			setEndsAt(endsAt);
			setRecurrenceFrequency(recurrenceFrequency);

			if (meeting?.series) {
				setConfirm(ConfirmActionType.Update);
				return;
			}

			await doUpsert(false, {
				startsAt,
				endsAt,
				recurrenceFrequency,
			});
		},
		[defaults.recurrenceFrequency, doUpsert, meeting?.series],
	);

	const attendeesDescription = useMemo(() => {
		const and = t('common.and').toLowerCase();
		const firstNames = props.attendees.map((user) => `${user.firstName}`);

		const firstFew = firstNames.slice(0, -1).join(', ');
		const last = firstNames[firstNames.length - 1];

		return firstFew ? `${firstFew} ${and} ${last}` : last;
	}, [props.attendees, t]);

	const disabled = meeting?.isCancelled || !props.canUpdate;

	return (
		<Modal>
			<Modal.Header title={props.title} onClose={props.onClose} />
			{meeting && !meeting.isCancelled && (
				<MeetingProposals
					meeting={meeting}
					canUpdate={props.canUpdate}
					onAccept={onAcceptProposal}
				/>
			)}
			<Modal.Contents>
				<FlexGap gap={16} flexDirection="column">
					{error && <GraphQLErrorMessage error={error} />}
					{meeting?.isCancelled && (
						<Callout color="warning">
							<FlexGap gap={8} alignItems="center" justifyContent="space-between" flexGrow={1}>
								<span>{t('meeting.cancelled')}</span>
								<Button
									type="inlineTextButton"
									title={t('meeting.restore')}
									onClick={async () => await restore()}
								/>
							</FlexGap>
						</Callout>
					)}
					{!props.canUpdate && (
						<Callout color="warning">
							{t('meeting.onlyCreatorCanEdit', {
								creatorFirstName: meeting?.createdBy.firstName,
							})}
						</Callout>
					)}
					<Box>
						<DateTimeInput value={startsAt} onChange={onChangeStartsAt} disabled={disabled} />
						<Paragraph mt={1} small textAlign="right">
							{meetingTimezone !== localTimezone &&
								t('meeting.timezoneExplanation', {
									location: meetingCity,
									dateTime: startsAtFormatted,
								})}
						</Paragraph>
					</Box>
					<DurationInput value={duration} onChange={onChangeDuration} disabled={disabled} />
					<Box mt={3}>
						<SeriesInput
							disabled={disabled}
							series={meeting?.series}
							recurrenceFrequency={recurrenceFrequency}
							onChangeRecurrenceFrequency={setRecurrenceFrequency}
							target={props.target}
							renderSeriesMetadataFields={props.renderSeriesMetadataFields}
						/>
					</Box>
				</FlexGap>
				<Separator mt={4} mb={3} />
				{props.attendeesLabel && (
					<Paragraph mb={2}>
						<i>{props.attendeesLabel}</i>
					</Paragraph>
				)}
				<FlexGap gap={12} alignItems="center">
					<Avatar.Stack
						users={props.attendees}
						shownAvatarAmount={props.attendees.length}
						borderColor={props.attendees.map(
							(attendee) =>
								ATTENDEE_STATUS_BORDER_COLOR[
									meeting?.attendees.find((a) => a.user.id === attendee.id)?.status ??
										AttendeeStatus.NeedsAction
								],
						)}
						borderWidth={2}
					/>
					<Paragraph color="antracite" large>
						{attendeesDescription}
					</Paragraph>
				</FlexGap>
			</Modal.Contents>
			{props.canUpdate && (
				<Modal.Footer flexDirection="column" alignItems="stretch">
					<Flex alignItems="center" justifyContent="flex-end" mb={3} width="100%">
						<Box mr={2}>
							<IconWrapper size={16} color="#249ee4">
								<MicrosoftOutlookIcon />
							</IconWrapper>
						</Box>
						<Box mr={2}>
							<IconWrapper size={14} color="black">
								<AppleLogoIcon />
							</IconWrapper>
						</Box>
						<Box mr={3}>
							<IconWrapper size={14} color="#4285f4">
								<GoogleLogoIcon />
							</IconWrapper>
						</Box>
						<Paragraph small>{t('meeting.modal.integratedWithCalendar')}</Paragraph>
					</Flex>

					<Flex width="100%" alignItems="center" justifyContent="flex-end">
						{meeting && !meeting.isCancelled && (
							<Button
								type="destructive"
								title={t('meeting.cancel')}
								mr="auto"
								onClick={async () =>
									meeting.series
										? setConfirm(ConfirmActionType.Cancel)
										: await cancel({ cancelSeries: false })
								}
							/>
						)}
						<Button type="secondary" title={t('common.cancel')} onClick={props.onClose} />
						<Button
							type="primary"
							title={t('modal.footer.form.save')}
							ml={2}
							onClick={onClickSave}
							disabled={disabled}
						/>
						<Popover
							backdrop
							center
							visible={confirm !== undefined}
							onDismiss={() => setConfirm(undefined)}
						>
							{confirm === ConfirmActionType.Update && (
								<MeetingSeriesConfirmationModal.Update
									onClose={() => setConfirm(undefined)}
									onOnlyThisMeeting={() => doUpsert(true)}
									onAllFutureMeetings={() => doUpsert(false)}
								/>
							)}
							{confirm === ConfirmActionType.Archive && (
								<MeetingSeriesConfirmationModal.Archive
									onClose={() => setConfirm(undefined)}
									onArchive={() => doUpsert(false)}
								/>
							)}
							{confirm === ConfirmActionType.Cancel && (
								<MeetingSeriesConfirmationModal.Cancel
									onClose={() => setConfirm(undefined)}
									onOnlyThisMeeting={() => cancel({ cancelSeries: false })}
									onAllFutureMeetings={() => cancel({ cancelSeries: true })}
								/>
							)}
						</Popover>
					</Flex>
				</Modal.Footer>
			)}
		</Modal>
	);
}

function useDuration(
	startsAt: moment.MomentInput,
	endsAt: moment.MomentInput,
	defaultDuration: number,
) {
	return useMemo(() => {
		const diff = moment(endsAt).diff(moment(startsAt), 'minutes');
		return isNaN(diff) ? defaultDuration : diff;
	}, [startsAt, endsAt, defaultDuration]);
}

function useFieldDefaults(meeting: MeetingFragment | undefined | null, defaultDuration: number) {
	const startsAt = useMemo(() => {
		const currentDate = new Date();
		const flooredMinutes = Math.floor(currentDate.getMinutes() / 15) * 15;

		return meeting?.startsAt
			? new Date(meeting.startsAt)
			: new Date(new Date().setMinutes(flooredMinutes, 0, 0));
	}, [meeting?.startsAt]);

	const endsAt = useMemo(() => {
		return meeting?.endsAt
			? new Date(meeting.endsAt)
			: moment(startsAt).add(defaultDuration, 'minutes').toDate();
	}, [meeting?.endsAt, startsAt, defaultDuration]);

	const duration = useDuration(startsAt, endsAt, defaultDuration);

	const recurrenceFrequency = meeting?.series?.recurrenceFrequency ?? undefined;

	return {
		startsAt,
		endsAt,
		duration,
		recurrenceFrequency,
	};
}
